/** Shown when an upload reports success but names no stored file. Reads as the
 *  parenthetical cause after a caller's own prefix (see
 *  `buildUserActionErrorMessage`), so it explains *why* without repeating that
 *  the picture is unchanged. */
export const PROFILE_PIC_UPLOAD_NO_URL =
  "the server accepted the image but didn't return a stored file — please try again, and let IT know if it keeps happening";

/**
 * Pulls the stored URL out of a profile-image upload response.
 *
 * `POST /uploadedFiles/user/:id/profile` is documented as returning `[{ url }]`,
 * and did in practice — existing avatars are `userProfiles/...` S3 URLs stored
 * through this path. It currently answers `200 {}`: a success status naming no
 * file, with nothing persisted (verified in the browser against the dev BFF —
 * `GET /uploadedFiles/:id/entityType/userProfiles` stays `[]`).
 *
 * That mattered because the old code read `uploadedFileResponses[0].url`
 * directly. Against `{}` that is a TypeError, and `UploadButton` swallows
 * rejections from its `setFileDetails` callback — so the upload failed in total
 * silence: brief spinner, old avatar back, no message anywhere.
 *
 * Accepts either the documented array or the `{ [key]: entry }` map the sibling
 * `/uploadedFiles/:id/entityType/:type` route returns, since both are live in
 * this codebase, and throws a message worth showing when neither yields a URL.
 * A thrown Error is deliberate: the caller must not persist a `blob:` preview or
 * an empty string over a good stored picture.
 */
export const readUploadedProfilePicUrl = (response: unknown): string => {
  let entries: unknown[] = [];
  if (Array.isArray(response)) {
    entries = response;
  } else if (response !== null && typeof response === 'object') {
    entries = Object.values(response as Record<string, unknown>);
  }

  const url = entries
    .map((entry) =>
      entry !== null && typeof entry === 'object' ? (entry as { url?: unknown }).url : undefined
    )
    .find((value): value is string => typeof value === 'string' && value.trim() !== '');

  if (!url) {
    throw new Error(PROFILE_PIC_UPLOAD_NO_URL);
  }
  return url;
};
